import { ChevronLeft, XClose } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { IconButton } from '@/lib/base/buttons/button-utility';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import {
  SurfaceDismissContext,
  type SurfaceDismissContextValue,
  useSurfaceDismiss,
} from '@/lib/base/overlays/surface-dismiss-context';
import { QueryErrorState } from '@/lib/components';

/**
 * The builder panel's chrome: the one place that decides how the user gets
 * out, for EVERY state the panel can be in.
 *
 * Below `lg` the panel is the whole screen above the chat dock and there is
 * no header, so a way back is the only way out — and the empty state is
 * exactly the "opened the builder, changed my mind" case. `onBack` is
 * published on `SurfaceDismissContext` so the record header (which renders
 * its own back chevron from that context once a record is showing) and the
 * pre-record states below share a single source. At `lg` and up
 * `BuilderChatHeader` owns navigation, `onBack` is undefined, and nothing
 * renders.
 *
 * Previously each pre-record state rendered its own back button, and the
 * error state was missed — the per-state duplication this replaces.
 */
export function BuilderPanelFrame({
  onBack,
  onClose,
  /** True for states that do not render `RecordHeader` (which has its own chevron). */
  showBack,
  children,
}: {
  onBack: (() => void) | undefined;
  /**
   * Dismiss for hosts where the panel is closable chrome (the chat's docked
   * pane) rather than the page itself. Published as the surface `close`, so
   * the record header's chrome close (`showCloseButton`) picks it up; the
   * pre-record states render their own X for it when there is no `back`.
   */
  onClose?: (() => void) | undefined;
  showBack: boolean;
  children: ReactNode;
}) {
  const dismiss = useMemo<SurfaceDismissContextValue | undefined>(() => {
    const close = onClose ?? onBack;
    if (!close) {
      return undefined;
    }
    return { close, back: onBack, isEntering: false, isExiting: false };
  }, [onBack, onClose]);
  return (
    <SurfaceDismissContext value={dismiss}>
      {/* `relative` anchors the floating SaveBar, exactly as the slideout's
          dialog does. Without it the bar would position against a further-out
          ancestor and drift off the panel. */}
      <div className='flex h-full w-full flex-col relative'>
        {showBack && <PanelDismissButton />}
        {children}
      </div>
    </SurfaceDismissContext>
  );
}

/**
 * The pre-record states' way out: a back chevron when the surface has a
 * `back` (builder mobile), otherwise the chrome X when it only has a `close`
 * (the chat's docked pane). One control, never both.
 */
function PanelDismissButton() {
  const surface = useSurfaceDismiss();
  if (surface?.back) {
    return (
      <div className='flex shrink-0 items-center px-4 py-3'>
        <IconButton
          onClick={surface.back}
          color='tertiary'
          size='xs'
          icon={ChevronLeft}
          aria-label='Go back'
        />
      </div>
    );
  }
  if (surface?.close) {
    return (
      <div className='flex shrink-0 items-center justify-end px-4 py-3'>
        <IconButton
          onClick={surface.close}
          color='tertiary'
          size='xs'
          icon={XClose}
          aria-label='Close panel'
          data-slideout-dismiss=''
        />
      </div>
    );
  }
  return null;
}

function SavingPanel() {
  return (
    <div className='flex flex-1 items-center justify-center px-6' role='status'>
      {/* Deliberately the neutral spinner, not the brand `ahoy` one — that
          is the "Thinking…" loader and belongs to the agent reasoning. This
          is the system persisting a decision the user has already made. */}
      <LoadingIndicator size='md' label='Saving…' />
    </div>
  );
}

function EmptyPanel() {
  return (
    <div className='flex flex-1 items-center justify-center px-6'>
      <p className='text-body-small text-center text-[var(--color-text-tertiary)]'>
        What you build will appear here as you describe it.
      </p>
    </div>
  );
}

/**
 * The builder-subject fetch itself failed — not "nothing proposed yet."
 * Rendered ahead of `EmptyPanel` so a real failure is never mistaken for the
 * normal pre-first-proposal state.
 */
function ErrorPanel({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  return (
    <div className='flex flex-1 items-center justify-center px-6'>
      <QueryErrorState
        error={error}
        label="Couldn't load what you built"
        onRetry={onRetry}
      />
    </div>
  );
}

export { EmptyPanel, ErrorPanel, SavingPanel };
